import { createSlice, current } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  isFailedJob,
  isIncompleteJob,
} from '@sonifi/wb-core/services/jobsHelper';
import { ASPECT_RATIO, PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import { findNodeById } from '@sonifi/wb-tree/utils/helper';
import {
  getPublishStatusMessage,
  filterModelRegions,
  updateScreenType,
  hiddenSectionsByMenuStyle,
  getHasUnpublishedChanges,
  calculateNumPages,
  configureHorizontalMenu,
  configureGridMenu,
  getNodeChildrenActive,
  getGridPageSize,
} from './hotelDirectorySliceHelper';
import { DEFAULT_LANGUAGE, DIRECTIONS, MENU_STYLE } from '../constants/wbHDContansts';

const initialState = {
  activeChild: 0,
  background: null,
  canPage: {
    down: false,
    left: false,
    right: false,
    up: false,
  },
  importModal: false,
  exportModal: false,
  children: [],
  compendiaMenu: {},
  deepLinkConfig: {
    showModal: false,
    item: null,
  },
  hasUnpublishedChanges: false,
  hiddenSections: [],
  highlightIndex: 0,
  isFailedJob: false,
  lastPublished: null,
  lastPublishJob: null,
  lastPublishUser: null,
  loading: LOADING_STATUS.notLoading,
  mediaDependencies: null,
  menuStyle: null,
  enableFullscreenImageRegions: false,
  enableQRCodes: false,
  nodePreviewSelected: null,
  platform: PLATFORMS.tv,
  productSettings: null,
  regionsPhone: [],
  regionsTv: [],
  regionsWorked: [],
  screen: null,
  screenFromNode: null,
  selectedLanguage: DEFAULT_LANGUAGE,
  visibleChildren: [],
};

export const hotelDirectorySlice = createSlice({
  name: 'hotelDirectory',
  initialState,
  reducers: {
    setHotelDirectortyMenu(state, action) {
      const {
        background,
        lastPublished,
        lastPublishUser,
        lastPublishJob,
        latestPublishJob,
        mediaDependencies,
        compendiaMenu,
        platform,
        productSettings,
      } = action.payload;
      state.background = background;
      state.productSettings = productSettings;
      state.compendiaMenu = compendiaMenu;
      state.menuStyle = productSettings.tv_menu_style;
      state.enableQRCodes = !!productSettings.enable_qr_codes;
      state.enableFullscreenImageRegions = !!productSettings.enable_fullscreen_image_regions;
      state.platform = platform;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      state.latestPublishJob = latestPublishJob;
      const incompleteJob = latestPublishJob
        ? isIncompleteJob(latestPublishJob)
        : null;

      const failedJob = lastPublishJob ? isFailedJob(lastPublishJob) : null;

      state.lastPublishJob = lastPublishJob;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        compendiaMenu,
        lastPublished: lastPublished ? new Date(lastPublished) : null,
        productSettings,
        background,
      });
      state.mediaDependencies = mediaDependencies;
    },
    changeScreenLanguage(state, action) {
      state.selectedLanguage = action.payload;
    },
    setLoadingHotelDirectory(state, action) {
      state.loading = action.payload;
    },
    setScreen(state, action) {
      state.screen = action.payload;
    },
    setNodePreviewSelected(state, action) {
      const { node } = action.payload;
      const { menuStyle } = current(state);

      state.highlightIndex = 0;

      state.nodePreviewSelected = node;
      state.screen = node.screen;
      state.regionsWorked = node.screen.regions;
      const type = updateScreenType(node.screen.regions);
      state.regionsTv = filterModelRegions({
        menuStyle,
        screen: node.screen,
        platform: PLATFORMS.tv,
        type,
      });

      state.regionsPhone = filterModelRegions({
        menuStyle,
        screen: node.screen,
        platform: PLATFORMS.phone,
        type,
      });
      state.type = type;

      state.hiddenSections = hiddenSectionsByMenuStyle({
        menuStyle,
      });

      // Reset page config
      state.editorStyleLeft = '0px';
      state.page = 1;
      state.canPage = {
        ...initialState.canPage,
        down: false,
        left: false,
        right: false,
        up: false,
      };
    },
    setRegionSelected(state, action) {
      const { region } = action.payload;
      const { menuStyle, nodePreviewSelected } = current(state);
      const hiddenSections = nodePreviewSelected.screen.regions.some((r) => (
        r.type === 'image' && r.content.some((c) => c.aspect_ratio === ASPECT_RATIO.FULLSCREEN.aspectRatio)
      ))
        ? ['single', 'multi']
        : nodePreviewSelected.screen.regions.some(
          (r) => r.type === 'menu' || r.type === 'text',
        )
          ? ['single', 'full']
          : ['multi', 'full'];

      state.hiddenSections = hiddenSectionsByMenuStyle({
        menuStyle,
        hiddenSections,
      });
      state.regionSelected = region;
    },
    setScreenFromNode(state, action) {
      const { fromNode } = action.payload;
      state.screenFromNode = fromNode;
    },
    setHDPlatform(state, action) {
      const { platform } = action.payload;
      state.platform = platform;
    },
    updateHDTree(state, action) {
      const { cleanRegion = true, compendiaMenu, screen } = action.payload;
      const { menuStyle, nodePreviewSelected } = current(state);
      state.screen = screen;
      state.compendiaMenu = compendiaMenu;

      const node = findNodeById({
        menu: compendiaMenu.menu,
        nodeId: nodePreviewSelected.id,
      });
      state.nodePreviewSelected = node;
      const type = updateScreenType(node.screen.regions);
      state.regionsTv = filterModelRegions({
        menuStyle,
        screen: node.screen,
        platform: PLATFORMS.tv,
        type,
      });
      state.regionsPhone = filterModelRegions({
        menuStyle,
        screen: node.screen,
        platform: PLATFORMS.phone,
        type,
      });
      state.type = type;

      // When Clean Region close Modal
      if (cleanRegion) {
        state.regionSelected = null;
      }
    },
    updateHDMediadependencies(state, action) {
      const { mediaDependencies } = action.payload;
      state.mediaDependencies = mediaDependencies;
    },
    updateJobsStatus(state, action) {
      const {
        latestPublishJob,
        lastPublishJob,
        lastPublished,
        lastPublishUser,
      } = action.payload;
      const { background, compendiaMenu, productSettings } = current(state);

      const incompleteJob = latestPublishJob
        ? isIncompleteJob(latestPublishJob)
        : null;

      const failedJob = lastPublishJob ? isFailedJob(lastPublishJob) : null;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        compendiaMenu,
        lastPublished: lastPublished ? new Date(lastPublished) : null,
        productSettings,
        background,
      });
    },
    setPublishingStatus(state, action) {
      const { job } = action.payload;
      const incompleteJob = job ? isIncompleteJob(job) : null;
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = false;
      state.lastPublishJob = job;
      state.lastPublished = job.last_updated;
      state.lastPublishUser = job._edited_by;
      state.hasUnpublishedChanges = false;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: job,
        isIncompleteJob: incompleteJob,
        isFailedJob: false,
      });
    },
    checkUnpublishedChanges(state) {
      const { background, compendiaMenu, lastPublished, productSettings } =
        current(state);
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        compendiaMenu,
        lastPublished: lastPublished ? new Date(lastPublished) : null,
        productSettings,
        background,
      });
    },
    setPageConfig(state, action) {
      const { menuStyle } = current(state);

      const {
        forText = false,
        forMenu = false,
        forServiceLink = false,
        keepHighlightIndex = false,
      } = action.payload;

      if (forText) {
        const { pageWidth, scrollWidth } = action.payload;
        const numPages = calculateNumPages({ scrollWidth, pageWidth });
        state.numPages = numPages;
        state.page = 1;
        state.pageWidth = pageWidth;
        state.canPage = {
          ...initialState.canPage,
          down: numPages > 1,
        };
      }

      if (forMenu) {
        const { nodePreviewSelected } = current(state);

        if (menuStyle === MENU_STYLE.horizontal) {
          const { children, visibleChildren, highlightIndex, activeChild } =
            configureHorizontalMenu(nodePreviewSelected);
          state.children = children;
          state.activeChild = activeChild;
          state.highlightIndex = highlightIndex;
          state.visibleChildren = visibleChildren;
          state.canPage = {
            ...initialState.canPage,
            left: true,
            right: true,
          };
        } else if (menuStyle === MENU_STYLE.grid || menuStyle === MENU_STYLE.simpleGrid) {
          const { highlightIndex, nodePreviewSelected } = current(state);
          const { children, numPages, pageSize } =
            configureGridMenu(nodePreviewSelected, menuStyle);
          state.numPages = numPages;
          state.page = 1;
          state.children = children;
          state.activeChild = 0;
          state.highlightIndex = keepHighlightIndex ? highlightIndex : 0;
          state.visibleChildren = children.slice(0, pageSize);
          state.canPage = {
            ...initialState.canPage,
            down: numPages > 1,
          };
        } else if (menuStyle === MENU_STYLE.vertical) {
          state.visibleChildren = getNodeChildrenActive(nodePreviewSelected);
        }
      }

      if (forServiceLink) {
        state.canPage = {
          ...initialState.canPage,
        };
      }
    },
    changeHDTvPage(state, action) {
      const { direction } = action.payload;

      if (direction === DIRECTIONS.down || direction === DIRECTIONS.up) {
        const { children, menuStyle, numPages, page, pageWidth } =
          current(state);
        const nextPage = direction === DIRECTIONS.down ? page + 1 : page - 1;
        if (nextPage > 0 && nextPage <= numPages) {
          const newPage = Math.max(1, Math.min(numPages, nextPage));
          state.editorStyleLeft = `${pageWidth * (nextPage - 1) * -1}px`;
          state.page = newPage;
          state.canPage = {
            ...initialState.canPage,
            up: newPage > 1,
            down: newPage < numPages && numPages > 1,
          };
          if (menuStyle === MENU_STYLE.grid || menuStyle === MENU_STYLE.simpleGrid) {
            const { nodePreviewSelected } = current(state);
            const pageSize = getGridPageSize(nodePreviewSelected, menuStyle);
            const from = pageSize * nextPage - pageSize;
            const to = pageSize * nextPage;
            state.visibleChildren = children.slice(from, to);
          }
        }
      } else {
        const { activeChild, children, highlightIndex, visibleChildren } =
          current(state);
        const totalChildren = children.length;
        const newVisibleChildren = [...visibleChildren];
        let newActiveChild = null;

        if (direction === DIRECTIONS.right) {
          newActiveChild = (activeChild + 1) % totalChildren;
          newVisibleChildren.shift();
          newVisibleChildren.push(
            children[(newActiveChild + highlightIndex) % totalChildren],
          );
        } else {
          // left
          newActiveChild = (activeChild || totalChildren) - 1;
          newVisibleChildren.pop();
          newVisibleChildren.unshift(
            children[
              (newActiveChild - highlightIndex + totalChildren) % totalChildren
            ],
          );
        }
        state.activeChild = newActiveChild;
        state.visibleChildren = newVisibleChildren;
      }
    },
    setNodeHightlight(state, action) {
      state.highlightIndex = action.payload;
    },
    setCompendiaMenu(state, action) {
      const { compendiaMenu } = action.payload;
      state.compendiaMenu = compendiaMenu;
    },
    updateRegionsWorked(state, action) {
      const { regions } = action.payload;
      const { regionsWorked } = current(state);

      state.regionsWorked = [
        ...regions,
        ...regionsWorked.filter(
          (rw) => !regions.find((r) => r.type === rw.type),
        ),
      ];
    },
    cleanHotelDirectorySlice() {
      return initialState;
    },
    setDeepLinkConfig(state, action) {
      state.deepLinkConfig = action.payload;
    },
    setImportModal(state, action) {
      state.importModal = action.payload;
    },
    setExportModal(state, action) {
      state.exportModal = action.payload;
    },
  },
});

export const {
  changeHDTvPage,
  changeScreenLanguage,
  checkUnpublishedChanges,
  cleanHotelDirectorySlice,
  setCompendiaMenu,
  setDeepLinkConfig,
  setExportModal,
  setHDPlatform,
  setHotelDirectortyMenu,
  setImportModal,
  setLoadingHotelDirectory,
  setNodeHightlight,
  setNodePreviewSelected,
  setPageConfig,
  setPublishingStatus,
  setRegionSelected,
  setScreen,
  setScreenFromNode,
  updateHDMediadependencies,
  updateHDTree,
  updateJobsStatus,
  updateRegionsWorked,
} = hotelDirectorySlice.actions;

export default hotelDirectorySlice.reducer;
